/**
 * Hook for efficiently fetching and managing tab counts on detail pages.
 * Provides counts for contacts, projects, documents, discussions, notes, and tasks.
 * Supports optimistic updates when records are added/removed locally.
 */

import { useCallback, useMemo } from 'react'
import { apiClient } from '@/lib/api/api-client'
import { useQuery, useQueryClient } from '@tanstack/react-query'

export interface TabCounts {
  communications?: number
  contacts?: number
  projects?: number
  documents?: number
  discussions?: number
  notes?: number
  tasks?: number
  expenses?: number
}

interface UseDetailTabCountsOptions {
  moduleType: 'entity' | 'project' | 'contact' | 'deal'
  moduleId: string
  enabled?: boolean
}

interface TabCountsResponse {
  communications?: number
  contacts?: number
  projects?: number
  documents?: number
  threads?: number
  notes?: number
  tasks?: number
  expenses?: number
}

// Query key factory for tab counts
const tabCountsKeys = {
  all: ['tab-counts'] as const,
  entity: (entityId: string) =>
    [...tabCountsKeys.all, 'entity', entityId] as const,
  project: (projectId: string) =>
    [...tabCountsKeys.all, 'project', projectId] as const,
  contact: (contactId: string) =>
    [...tabCountsKeys.all, 'contact', contactId] as const,
  deal: (dealId: string) => [...tabCountsKeys.all, 'deal', dealId] as const,
}

/**
 * Fetches tab counts for a module (entity, project, contact, or deal)
 */
async function fetchTabCounts(
  moduleType: 'entity' | 'project' | 'contact' | 'deal',
  moduleId: string,
): Promise<TabCountsResponse> {
  let endpoint: string
  switch (moduleType) {
    case 'entity':
      endpoint = `/entities/${moduleId}/counts`
      break
    case 'project':
      endpoint = `/projects/${moduleId}/counts`
      break
    case 'contact':
      endpoint = `/contacts/${moduleId}/counts`
      break
    case 'deal':
      endpoint = `/deals/${moduleId}/counts`
      break
  }

  return apiClient<TabCountsResponse>(endpoint)
}

/**
 * Hook for fetching and managing tab counts on detail pages.
 *
 * @example
 * ```tsx
 * const { counts, updateCount, incrementCount, decrementCount } = useDetailTabCounts({
 *   moduleType: 'entity',
 *   moduleId: entityId,
 * });
 *
 * // Use counts in tabs
 * const tabs = getEntityDetailTabs(checkFeature, counts);
 *
 * // Optimistically update when adding a contact
 * incrementCount('contacts');
 * ```
 */
export function useDetailTabCounts({
  moduleType,
  moduleId,
  enabled = true,
}: UseDetailTabCountsOptions) {
  const queryClient = useQueryClient()

  const queryKey = (() => {
    switch (moduleType) {
      case 'entity':
        return tabCountsKeys.entity(moduleId)
      case 'project':
        return tabCountsKeys.project(moduleId)
      case 'contact':
        return tabCountsKeys.contact(moduleId)
      case 'deal':
        return tabCountsKeys.deal(moduleId)
    }
  })()

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => fetchTabCounts(moduleType, moduleId),
    enabled: enabled && !!moduleId,
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  })

  // Transform API response to TabCounts interface
  const counts: TabCounts = useMemo(
    () => ({
      communications: data?.communications,
      contacts: data?.contacts,
      projects: data?.projects,
      documents: data?.documents,
      discussions: data?.threads,
      notes: data?.notes,
      tasks: data?.tasks,
      expenses: data?.expenses,
    }),
    [data],
  )

  /**
   * Update a specific count optimistically
   */
  const updateCount = useCallback(
    (key: keyof TabCounts, value: number) => {
      queryClient.setQueryData<TabCountsResponse>(queryKey, old => {
        if (!old) return old
        const apiKey = key === 'discussions' ? 'threads' : key
        return { ...old, [apiKey]: value }
      })
    },
    [queryClient, queryKey],
  )

  /**
   * Increment a count by 1 (for optimistic adds)
   */
  const incrementCount = useCallback(
    (key: keyof TabCounts) => {
      queryClient.setQueryData<TabCountsResponse>(queryKey, old => {
        if (!old) return old
        const apiKey = key === 'discussions' ? 'threads' : key
        const currentValue = old[apiKey as keyof TabCountsResponse] ?? 0
        return { ...old, [apiKey]: currentValue + 1 }
      })
    },
    [queryClient, queryKey],
  )

  /**
   * Decrement a count by 1 (for optimistic removes)
   */
  const decrementCount = useCallback(
    (key: keyof TabCounts) => {
      queryClient.setQueryData<TabCountsResponse>(queryKey, old => {
        if (!old) return old
        const apiKey = key === 'discussions' ? 'threads' : key
        const currentValue = old[apiKey as keyof TabCountsResponse] ?? 0
        return { ...old, [apiKey]: Math.max(0, currentValue - 1) }
      })
    },
    [queryClient, queryKey],
  )

  /**
   * Invalidate and refetch counts
   */
  const refetchCounts = useCallback(() => {
    queryClient.invalidateQueries({ queryKey })
  }, [queryClient, queryKey])

  return {
    counts,
    isLoading,
    error,
    updateCount,
    incrementCount,
    decrementCount,
    refetchCounts,
  }
}

// Export query keys for external use
export { tabCountsKeys }
